import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    FiBell,
    FiCalendar,
    FiCheck,
    FiCheckCircle,
    FiCreditCard,
    FiDollarSign,
    FiFileText,
    FiLock,
    FiMessageCircle,
    FiSettings,
    FiShield,
    FiUser,
    FiUsers,
} from 'react-icons/fi';
import { Header } from '../../components/layout/Header';
import { Sidebar } from '../../components/layout/Sidebar';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { SearchableSelect } from '../../components/ui';
import { PaymentChargeModal } from '../../components/ui/PaymentChargeModal/PaymentChargeModal';
import { useAuth } from '../../contexts/AuthContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { api } from '../../services/api';
import styles from './Settings.module.css';
import { PerfisDaEmpresa, UsuariosDaEmpresa } from './AcessosDaEmpresa';
import { ehPlanoAnual } from '../../utils/plano';

type SettingsTab = 'profile' | 'preferences' | 'notifications' | 'security' | 'subscription' | 'payments' | 'usuarios' | 'perfis';

const FISCAL_KEY = 'app_fiscal_data';

/** Tipo de cada pagamento no historico (troca de plano comprada no PAP aparece como Upgrade). */
const TIPO_DE_PAGAMENTO: Record<string, string> = {
    CREATED: 'Novo',
    RENEWED: 'Renovação',
    UPGRADED: 'Upgrade',
    DOWNGRADED: 'Troca de plano',
};

const FEATURE_LABELS: Record<string, string> = {
    dashboard: 'Dashboard',
    graficos: 'Gráficos',
    accounts: 'Contas',
    transactions: 'Transações',
    cashflow: 'Fluxo de Caixa',
    reports: 'Relatórios',
    settings: 'Configurações',
    import: 'Importação de Planilha',
};


interface NotifPrefs {
    emailNotifications: boolean;
    pushNotifications: boolean;
    weeklyReport: boolean;
    budgetAlerts: boolean;
}

interface FiscalData {
    razaoSocial: string;
    cpfCnpj: string;
    endereco: string;
    cep: string;
}


export const Settings = () => {
    const { user, currentPlan, updateUser, empresaAtiva, podeGerirAcessos } = useAuth();

    const [searchParams] = useSearchParams();
    const validTabs: SettingsTab[] = [
        'profile', 'preferences', 'notifications', 'security', 'subscription', 'payments',
        ...(podeGerirAcessos ? (['usuarios', 'perfis'] as SettingsTab[]) : []),
    ];
    const tabParam = searchParams.get('tab') as SettingsTab | null;
    const [activeTab, setActiveTab] = useState<SettingsTab>(
        tabParam && validTabs.includes(tabParam) ? tabParam : 'profile',
    );

    const [profileName, setProfileName] = useState(user?.name ?? '');
    const [profileEmail, setProfileEmail] = useState(user?.email ?? '');
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const defaultNotif: NotifPrefs = {
        emailNotifications: true,
        pushNotifications: false,
        weeklyReport: true,
        budgetAlerts: true,
    };
    const [notif, setNotif] = useState<NotifPrefs>(defaultNotif);
    const [notifSaved, setNotifSaved] = useState(false);

    const defaultFiscal: FiscalData = { razaoSocial: user?.name ?? '', cpfCnpj: '', endereco: '', cep: '' };
    const [fiscal, setFiscal] = useState<FiscalData>(() => {
        try {
            const raw = localStorage.getItem(FISCAL_KEY);
            return raw ? { ...defaultFiscal, ...JSON.parse(raw) } : defaultFiscal;
        } catch {
            return defaultFiscal;
        }
    });
    const [fiscalSaving] = useState(false);
    const [fiscalMsg, setFiscalMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const { prefs, updatePref, ratesLoading, ratesError, formatCurrency, t } = usePreferences();
    const [prefsSaved, setPrefsSaved] = useState(false);

    const handleUpdatePref = <K extends keyof typeof prefs>(key: K, value: (typeof prefs)[K]) => {
        updatePref(key, value);
        setPrefsSaved(true);
        setTimeout(() => setPrefsSaved(false), 2000);
    };

    const handleSaveProfile = async () => {
        setProfileMsg(null);
        if (!profileName.trim()) return setProfileMsg({ type: 'error', text: t('settings.nomeObrigatorio') });
        if (!profileEmail.trim()) return setProfileMsg({ type: 'error', text: t('settings.emailObrigatorio') });
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileEmail.trim()))
            return setProfileMsg({ type: 'error', text: t('settings.emailInvalido') });

        setProfileSaving(true);
        try {
            await api.updateProfile({ name: profileName.trim(), email: profileEmail.trim() });
            updateUser({ name: profileName.trim(), email: profileEmail.trim() });
            setProfileMsg({ type: 'success', text: t('settings.perfilSalvo') });
        } catch (err: any) {
            setProfileMsg({ type: 'error', text: err.message || t('settings.erroSalvarPerfil') });
        } finally {
            setProfileSaving(false);
        }
    };

    const handleChangePassword = async () => {
        setPasswordMsg(null);
        if (!currentPassword || !newPassword || !confirmPassword)
            return setPasswordMsg({ type: 'error', text: t('settings.preencherCampos') });
        if (newPassword.length < 6)
            return setPasswordMsg({ type: 'error', text: t('settings.senhaMinCarac') });
        if (newPassword !== confirmPassword)
            return setPasswordMsg({ type: 'error', text: t('settings.senhasNaoCoincidem') });

        setPasswordSaving(true);
        try {
            await api.changePassword({ currentPassword, newPassword });
            setPasswordMsg({ type: 'success', text: t('settings.senhaAlterada') });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err: any) {
            setPasswordMsg({ type: 'error', text: err.message || t('settings.erroAlterarSenha') });
        } finally {
            setPasswordSaving(false);
        }
    };


    useEffect(() => {
        api.getNotificationPrefs().then(data => {
            if (Object.keys(data).length > 0) {
                setNotif(prev => ({ ...prev, ...data }));
            }
        }).catch(() => { });
    }, []);

    const updateNotif = async <K extends keyof NotifPrefs>(key: K, value: NotifPrefs[K]) => {
        const next = { ...notif, [key]: value };
        setNotif(next);
        setNotifSaved(true);
        setTimeout(() => setNotifSaved(false), 2000);
        try {
            await api.updateNotificationPrefs(next);
        } catch {
            // opcional: reverter estado em caso de erro
        }
    };

    type PaymentHistory = Awaited<ReturnType<typeof api.getMyPaymentHistory>>;
    const [paymentHistory, setPaymentHistory] = useState<PaymentHistory | null>(null);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [chargeModalOpen, setChargeModalOpen] = useState(false);

    type SubStatus = Awaited<ReturnType<typeof api.getMySubscriptionStatus>>;
    const [subStatus, setSubStatus] = useState<SubStatus | null>(null);

    useEffect(() => {
        if (activeTab !== 'payments') return;
        setPaymentLoading(true);
        api.getMyPaymentHistory()
            .then(setPaymentHistory)
            .catch(() => { })
            .finally(() => setPaymentLoading(false));
    }, [activeTab]);

    useEffect(() => {
        if (activeTab !== 'subscription') return;
        api.getMySubscriptionStatus()
            .then(setSubStatus)
            .catch(() => { });
    }, [activeTab]);

    const handleSaveFiscal = () => {
        localStorage.setItem(FISCAL_KEY, JSON.stringify(fiscal));
        setFiscalMsg({ type: 'success', text: t('settings.dadosFiscaisSalvos') });
        setTimeout(() => setFiscalMsg(null), 3000);
    };

    const navItems = [
        { id: 'profile' as SettingsTab, icon: <FiUser size={18} />, label: t('settings.perfil') },
        { id: 'preferences' as SettingsTab, icon: <FiSettings size={18} />, label: t('settings.preferencias') },
        { id: 'notifications' as SettingsTab, icon: <FiBell size={18} />, label: t('settings.notificacoes') },
        { id: 'security' as SettingsTab, icon: <FiLock size={18} />, label: t('settings.seguranca') },
        { id: 'subscription' as SettingsTab, icon: <FiCreditCard size={18} />, label: t('settings.assinatura') },
        { id: 'payments' as SettingsTab, icon: <FiFileText size={18} />, label: t('settings.historicoPagamentos') },
        // Gestão de acessos: só o dono da empresa ativa (decisão 2 do plano de convites)
        // e só se o plano tem a feature `usuarios`. O resto de Configurações vale para todo plano.
        ...(podeGerirAcessos
            ? [
                { id: 'usuarios' as SettingsTab, icon: <FiUsers size={18} />, label: 'Usuários' },
                { id: 'perfis' as SettingsTab, icon: <FiShield size={18} />, label: 'Perfis de acesso' },
            ]
            : []),
    ];

    // Trocou para uma empresa de que não é dono (ou sem a feature) estando numa dessas abas: volta ao perfil.
    useEffect(() => {
        if (!podeGerirAcessos && (activeTab === 'usuarios' || activeTab === 'perfis')) {
            setActiveTab('profile');
        }
    }, [podeGerirAcessos, activeTab]);

    const renderFeedback = (msg: { type: 'success' | 'error'; text: string } | null) => {
        if (!msg) return null;
        return (
            <p style={{
                fontSize: '0.875rem',
                color: msg.type === 'success' ? 'var(--color-success-dark)' : 'var(--color-error-dark)',
                marginTop: '0.5rem',
            }}>
                {msg.text}
            </p>
        );
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'usuarios':
                return podeGerirAcessos && empresaAtiva ? (
                    <>
                        <h3>Usuários</h3>
                        <UsuariosDaEmpresa empresa={empresaAtiva} />
                    </>
                ) : null;
            case 'perfis':
                return podeGerirAcessos && empresaAtiva ? (
                    <>
                        <h3>Perfis de acesso</h3>
                        <PerfisDaEmpresa empresa={empresaAtiva} />
                    </>
                ) : null;

            case 'profile':
                return (
                    <>
                        <h3 className={styles.sectionTitle}>{t('settings.infosPerfil')}</h3>

                        <div className={styles.profileSection}>
                            <div className={styles.avatar}>
                                {user?.name?.charAt(0)?.toUpperCase() ?? 'U'}
                            </div>
                            <div className={styles.profileInfo}>
                                <h3>{user?.name ?? 'Usuário'}</h3>
                                <p>{user?.email ?? 'usuario@email.com'}</p>
                            </div>
                        </div>

                        <div className={styles.formGrid}>
                            <div className={styles.formGroup}>
                                <Input
                                    label={t('settings.nomeCompleto')}
                                    value={profileName}
                                    onChange={(e) => setProfileName(e.target.value)}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <Input
                                    label={t('login.email')}
                                    type="email"
                                    value={profileEmail}
                                    onChange={(e) => setProfileEmail(e.target.value)}
                                />
                            </div>
                        </div>

                        {renderFeedback(profileMsg)}

                        <div className={styles.actions}>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setProfileName(user?.name ?? '');
                                    setProfileEmail(user?.email ?? '');
                                    setProfileMsg(null);
                                }}
                            >
                                {t('common.cancelar')}
                            </Button>
                            <Button onClick={handleSaveProfile} disabled={profileSaving}>
                                {profileSaving ? t('common.salvando') : t('common.salvarAlteracoes')}
                            </Button>
                        </div>
                    </>
                );

            case 'preferences':
                return (
                    <>
                        <h3 className={styles.sectionTitle}>{t('settings.prefSistema')}</h3>

                        {prefsSaved && (
                            <p style={{ fontSize: '0.8rem', color: 'var(--color-success-dark)', marginBottom: '0.75rem' }}>
                                {t('settings.prefsSalvas')}
                            </p>
                        )}

                        <div className={styles.settingGroup}>
                            <div className={styles.settingRow}>
                                <div className={styles.settingInfo}>
                                    <h4>{t('settings.modoEscuro')}</h4>
                                    <p>{t('settings.modoEscuroDesc')}</p>
                                </div>
                                <button
                                    className={`${styles.toggle} ${prefs.darkMode ? styles.active : ''}`}
                                    onClick={() => handleUpdatePref('darkMode', !prefs.darkMode)}
                                    aria-pressed={prefs.darkMode}
                                    aria-label={t('settings.modoEscuro')}
                                />
                            </div>

                            <div className={styles.settingRow}>
                                <div className={styles.settingInfo}>
                                    <h4>{t('settings.moedaPadrao')}</h4>
                                    <p>{t('settings.moedaPadraoDesc')}</p>
                                </div>
                                <SearchableSelect
                                    className={styles.selectSmall}
                                    value={prefs.currency}
                                    onChange={(v) => handleUpdatePref('currency', v)}
                                    disabled={ratesLoading}
                                    options={[
                                        { value: 'BRL', label: 'R$ Real (BRL)' },
                                        { value: 'USD', label: '$ Dólar (USD)' },
                                        { value: 'EUR', label: '€ Euro (EUR)' },
                                    ]}
                                />

                                {ratesLoading && (
                                    <p style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
                                        Buscando cotação atualizada...
                                    </p>
                                )}
                                {ratesError && (
                                    <p style={{ fontSize: '0.75rem', color: 'var(--color-warning-main)', marginTop: '0.25rem' }}>
                                        Não foi possível buscar a cotação. Os valores podem não estar convertidos.
                                    </p>
                                )}
                            </div>

                            <div className={styles.settingRow}>
                                <div className={styles.settingInfo}>
                                    <h4>{t('settings.idioma')}</h4>
                                    <p>{t('settings.idiomaDesc')}</p>
                                </div>
                                <SearchableSelect
                                    className={styles.selectSmall}
                                    value={prefs.language}
                                    onChange={(v) => handleUpdatePref('language', v)}
                                    options={[
                                        { value: 'pt-BR', label: 'Português (BR)' },
                                        { value: 'en-US', label: 'English (US)' },
                                        { value: 'es', label: 'Español' },
                                    ]}
                                />
                            </div>

                            <div className={styles.settingRow}>
                                <div className={styles.settingInfo}>
                                    <h4>{t('settings.primeiroDia')}</h4>
                                    <p>{t('settings.primeiroDiaDesc')}</p>
                                </div>
                                <SearchableSelect
                                    className={styles.selectSmall}
                                    value={prefs.firstDayOfWeek}
                                    onChange={(v) => handleUpdatePref('firstDayOfWeek', v)}
                                    options={[
                                        { value: 'sunday', label: t('settings.domingo') },
                                        { value: 'monday', label: t('settings.segunda') },
                                    ]}
                                />
                            </div>
                        </div>
                    </>
                );

            case 'notifications':
                return (
                    <>
                        <h3 className={styles.sectionTitle}>{t('settings.notifConfig')}</h3>

                        {notifSaved && (
                            <p style={{ fontSize: '0.8rem', color: 'var(--color-success-dark)', marginBottom: '0.75rem' }}>
                                {t('settings.notifSalvas')}
                            </p>
                        )}

                        <div className={styles.settingGroup}>
                            {(
                                [
                                    { key: 'emailNotifications', label: t('settings.notifEmail'), desc: t('settings.notifEmailDesc') },
                                    { key: 'pushNotifications', label: t('settings.notifPush'), desc: t('settings.notifPushDesc') },
                                    { key: 'weeklyReport', label: t('settings.relSemanal'), desc: t('settings.relSemanalDesc') },
                                    { key: 'budgetAlerts', label: t('settings.alertasOrc'), desc: t('settings.alertasOrcDesc') },
                                ] as { key: keyof NotifPrefs; label: string; desc: string }[]
                            ).map(({ key, label, desc }) => (
                                <div key={key} className={styles.settingRow}>
                                    <div className={styles.settingInfo}>
                                        <h4>{label}</h4>
                                        <p>{desc}</p>
                                    </div>
                                    <button
                                        className={`${styles.toggle} ${notif[key] ? styles.active : ''}`}
                                        onClick={() => updateNotif(key, !notif[key])}
                                        aria-pressed={notif[key]}
                                        aria-label={label}
                                    />
                                </div>
                            ))}
                        </div>
                    </>
                );

            case 'security':
                return (
                    <>
                        <h3 className={styles.sectionTitle}>{t('settings.segConta')}</h3>

                        <div className={styles.formGrid}>
                            <div className={styles.formGroup}>
                                <Input
                                    label={t('settings.senhaAtual')}
                                    type="password"
                                    placeholder="••••••••"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                />
                            </div>
                            <div className={styles.formGroup} />
                            <div className={styles.formGroup}>
                                <Input
                                    label={t('settings.novaSenha')}
                                    type="password"
                                    placeholder="••••••••"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <Input
                                    label={t('settings.confirmarSenha')}
                                    type="password"
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />
                            </div>
                        </div>

                        {renderFeedback(passwordMsg)}

                        <div className={styles.actions}>
                            <Button onClick={handleChangePassword} disabled={passwordSaving}>
                                {passwordSaving ? t('settings.alterando') : t('settings.alterarSenha')}
                            </Button>
                        </div>

                        <div className={styles.supportSection}>
                                <h4><FiMessageCircle size={16} /> {t('settings.suporteTitle')}</h4>
                                <p>{t('settings.suporteDesc')}</p>
                                <a
                                    href="https://wa.me/5511936238210?text=Quero%20ajuda%20com%20o%20Financeiro%20Di%C3%A1rio"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.supportBtn}
                                >
                                    <FiMessageCircle size={16} />
                                    {t('settings.falarComSuporte')}
                                </a>
                            </div>
                    </>
                );

            case 'subscription':
                return (
                    <>
                        <h3 className={styles.sectionTitle}>{t('settings.suaAssinatura')}</h3>

                        {currentPlan ? (
                            <div className={styles.planDetailCard}>
                                <div className={styles.planDetailHeader}>
                                    <div>
                                        <span className={styles.planBadge}>{t('settings.planoAtual')}</span>
                                        <h3 className={styles.planDetailName}>{currentPlan.name}</h3>
                                        {currentPlan.description && (
                                            <p className={styles.planDetailDescription}>{currentPlan.description}</p>
                                        )}
                                    </div>
                                    <div className={styles.planDetailPrice}>
                                        <span className={styles.priceValue}>{formatCurrency(currentPlan.price)}</span>
                                        <span className={styles.pricePeriod}>
                                            /{t(ehPlanoAnual(currentPlan.interval) ? 'settings.ano' : 'settings.mes')}
                                        </span>
                                        <span className={styles.priceRecorrencia}>
                                            {t(ehPlanoAnual(currentPlan.interval) ? 'settings.cobAnual' : 'settings.cobMensal')}
                                        </span>
                                    </div>
                                </div>

                                <div className={styles.planDetailMeta}>
                                    <div className={styles.planDetailMetaItem}>
                                        <span className={styles.metaLabel}>{t('settings.transacoesPorMes')}</span>
                                        <span className={styles.metaValue}>
                                            {currentPlan.maxTransactionsPerMonth === -1
                                                ? t('common.ilimitado')
                                                : currentPlan.maxTransactionsPerMonth.toLocaleString('pt-BR')}
                                        </span>
                                    </div>
                                    {subStatus?.currentPeriodEnd && (
                                        <div className={styles.planDetailMetaItem}>
                                            <span className={styles.metaLabel}>Encerramento do ciclo</span>
                                            <span className={styles.metaValue}>
                                                {new Date(subStatus.currentPeriodEnd).toLocaleDateString('pt-BR')}
                                            </span>
                                        </div>
                                    )}
                                    {subStatus?.daysUntilExpiry != null && (
                                        <div className={styles.planDetailMetaItem}>
                                            <span className={styles.metaLabel}>Tempo até expirar</span>
                                            <span
                                                className={styles.metaValue}
                                                style={{
                                                    color: subStatus.daysUntilExpiry < 0
                                                        ? 'var(--color-error-dark)'
                                                        : subStatus.daysUntilExpiry <= 7
                                                            ? 'var(--color-warning-dark)'
                                                            : undefined,
                                                }}
                                            >
                                                {(() => {
                                                    const d = subStatus.daysUntilExpiry!;
                                                    if (d < 0) return `Expirou há ${Math.abs(d)} dia${Math.abs(d) === 1 ? '' : 's'}`;
                                                    if (d === 0) return 'Expira hoje';
                                                    return `Faltam ${d} dia${d === 1 ? '' : 's'}`;
                                                })()}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {currentPlan.features.length > 0 && (
                                    <div>
                                        <h4 className={styles.planDetailFeaturesTitle}>{t('settings.funcionalidades')}</h4>
                                        <div className={styles.planDetailFeatureGrid}>
                                            {currentPlan.features.map((f) => (
                                                <span key={f} className={styles.planDetailFeatureTag}>
                                                    <FiCheck size={11} />
                                                    {FEATURE_LABELS[f] ?? f}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', margin: '1rem 0 1.5rem' }}>
                                {t('settings.semPlanoAtivo')}
                            </p>
                        )}

                        {/* A compra no PAP casa com a conta pelo e-mail: outro e-mail
                            cria uma conta nova em vez de mudar o plano desta. */}
                        <div className={styles.cancelSection}>
                            <h4>Assinar ou mudar de plano</h4>
                            <p>
                                Na compra, use o mesmo e-mail desta conta: <strong>{user?.email}</strong>. Uma compra
                                com outro e-mail cria uma conta separada, sem os seus dados. O plano comprado vale
                                para a sua empresa; as empresas em que você é convidado seguem o plano de quem as paga.
                            </p>
                        </div>

                        {currentPlan && (
                            <div className={styles.cancelSection}>
                                <h4>Pagamento</h4>
                                <p>Gere ou visualize a cobrança da sua mensalidade (PIX ou cartão).</p>
                                <Button onClick={() => setChargeModalOpen(true)}>
                                    Gerar pagamento
                                </Button>
                            </div>
                        )}

                        {/* <div className={styles.cancelSection}>
                            <h4>{t('settings.cancelarAssinatura')}</h4>
                            <p>{t('settings.cancelarAssinaturaDesc')}</p>
                            <button
                                className={styles.cancelBtn}
                                onClick={() => window.open(
                                    'https://api.whatsapp.com/send/?phone=558587069521&text=Oi%2C+preciso+cancelar+minha+assinatura+do+sistema+financeiro+diario%21&type=phone_number&app_absent=0',
                                    '_blank',
                                    'noopener,noreferrer',
                                )}
                            >
                                {t('settings.solicitarCancelamento')}
                            </button>
                        </div> */}
                    </>
                );

            case 'payments': {
                const statusLabel = (() => {
                    if (!paymentHistory) return currentPlan ? t('settings.emDia') : t('settings.semPlano');
                    switch (paymentHistory.status) {
                        case 'ACTIVE': return t('settings.emDia');
                        case 'PAST_DUE': return 'Inadimplente';
                        case 'CANCELED': return 'Cancelado';
                        case 'SUSPENDED': return 'Suspenso';
                        default: return t('settings.semPlano');
                    }
                })();
                return (
                    <>
                        <h3 className={styles.sectionTitle}>{t('settings.historicoPagamentos')}</h3>

                        <div className={styles.paymentsSummary}>
                            <div className={styles.summaryCard}>
                                <span className={styles.summaryIcon}><FiDollarSign size={24} /></span>
                                <div className={styles.summaryInfo}>
                                    <span className={styles.summaryValue}>
                                        {paymentLoading ? '…' : paymentHistory ? formatCurrency(paymentHistory.totalPaid) : '—'}
                                    </span>
                                    <span className={styles.summaryLabel}>{t('settings.totalPago')}</span>
                                </div>
                            </div>
                            <div className={styles.summaryCard}>
                                <span className={styles.summaryIcon}><FiCalendar size={24} /></span>
                                <div className={styles.summaryInfo}>
                                    <span className={styles.summaryValue}>
                                        {paymentLoading ? '…' : paymentHistory ? paymentHistory.count : '—'}
                                    </span>
                                    <span className={styles.summaryLabel}>{t('settings.faturasPagas')}</span>
                                </div>
                            </div>
                            <div className={styles.summaryCard}>
                                <span className={styles.summaryIcon}><FiCheckCircle size={24} /></span>
                                <div className={styles.summaryInfo}>
                                    <span className={styles.summaryValue}>{statusLabel}</span>
                                    <span className={styles.summaryLabel}>{t('settings.statusConta')}</span>
                                </div>
                            </div>
                        </div>

                        {!paymentLoading && paymentHistory && paymentHistory.invoices.length > 0 ? (
                            <div className={styles.invoicesTable}>
                                <div className={styles.invoiceHeader}>
                                    <span>Data</span>
                                    <span>Plano</span>
                                    <span>Valor</span>
                                    <span>Tipo</span>
                                    <span>ID</span>
                                </div>
                                {paymentHistory.invoices.map(inv => (
                                    <div key={inv.id} className={styles.invoiceRow}>
                                        <span>{new Date(inv.date).toLocaleDateString('pt-BR')}</span>
                                        <span>{inv.planName}</span>
                                        <span>{formatCurrency(inv.amount)}</span>
                                        <span>{TIPO_DE_PAGAMENTO[inv.type] ?? inv.type}</span>
                                        <span style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)' }}>
                                            {inv.transactionId ? inv.transactionId.slice(0, 8) + '…' : '—'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            !paymentLoading && (
                                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', margin: '1rem 0 1.5rem' }}>
                                    {t('settings.historicoVazio')}
                                </p>
                            )
                        )}

                        <h4 className={styles.subsectionTitle}>{t('settings.dadosFiscais')}</h4>
                        <div className={styles.fiscalData}>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <Input
                                        label={t('settings.nomeRazaoSocial')}
                                        value={fiscal.razaoSocial}
                                        onChange={(e) => setFiscal(f => ({ ...f, razaoSocial: e.target.value }))}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <Input
                                        label="CPF/CNPJ"
                                        placeholder="000.000.000-00"
                                        value={fiscal.cpfCnpj}
                                        onChange={(e) => setFiscal(f => ({ ...f, cpfCnpj: e.target.value }))}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <Input
                                        label={t('settings.endereco')}
                                        placeholder="Rua, número, complemento"
                                        value={fiscal.endereco}
                                        onChange={(e) => setFiscal(f => ({ ...f, endereco: e.target.value }))}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <Input
                                        label={t('settings.cep')}
                                        placeholder="00000-000"
                                        value={fiscal.cep}
                                        onChange={(e) => setFiscal(f => ({ ...f, cep: e.target.value }))}
                                    />
                                </div>
                            </div>
                            {renderFeedback(fiscalMsg)}
                            <div className={styles.actions}>
                                <Button onClick={handleSaveFiscal} disabled={fiscalSaving}>
                                    {fiscalSaving ? t('common.salvando') : t('settings.salvarDadosFiscais')}
                                </Button>
                            </div>
                        </div>
                    </>
                );
            }
        }
    };

    return (
        <div className={styles.layout}>
            <Sidebar />
            <main className={styles.main}>
                <Header />
                <div className={styles.content}>
                    <div className={styles.pageHeader}>
                        <h2>{t('settings.titulo')}</h2>
                        <p>{t('settings.subtitulo')}</p>
                    </div>

                    <div className={styles.settingsGrid}>
                        <nav className={styles.settingsNav}>
                            {navItems.map(item => (
                                <button
                                    key={item.id}
                                    className={`${styles.navItem} ${activeTab === item.id ? styles.active : ''}`}
                                    onClick={() => setActiveTab(item.id)}
                                >
                                    <span>{item.icon}</span>
                                    {item.label}
                                </button>
                            ))}
                        </nav>

                        <div className={styles.settingsContent}>
                            {renderContent()}
                        </div>
                    </div>
                </div>
            </main>

            <PaymentChargeModal isOpen={chargeModalOpen} onClose={() => setChargeModalOpen(false)} />
        </div>
    );
};
